import type { MapStore } from "nanostores";

import { useStore } from "@nanostores/react";
import { debounce } from "perfect-debounce";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MAX_DEFAULT_ITEMS = 10;
const DEFAULT_MAX_SEARCH_ITEMS = 10;

export type ResourceAsyncSelectCacheMessages = {
  fetchSelected: string;
  loadInitial: string;
  search: string;
};

export type ResourceAsyncSelectCacheMessageOptions = {
  /**
   * Inserted after "Failed to load " and before the plural noun
   * (e.g. `"top "` → "Failed to load top entities:").
   */
  loadInitialPrefix?: string;
};

export const buildResourceAsyncSelectCacheMessages = (
  singularNoun: string,
  options?: ResourceAsyncSelectCacheMessageOptions,
): ResourceAsyncSelectCacheMessages => {
  const plural = pluralize(singularNoun);
  const loadInitialPrefix = options?.loadInitialPrefix ?? "";
  return {
    fetchSelected: `Failed to fetch selected ${singularNoun}:`,
    loadInitial: `Failed to load ${loadInitialPrefix}${plural}:`,
    search: `Failed to search ${plural}:`,
  };
};

export type ResourceAsyncSelectCacheConfig<T extends { id: string }> = {
  debounceMs?: number;
  fetchById: (id: string) => Promise<null | T | undefined>;
  loadDefaultBatch: () => Promise<T[]>;
  maxDefaultItems?: number;
  maxSearchItems?: number;
  messages: ResourceAsyncSelectCacheMessages;
  search: (searchTerm: string) => Promise<T[]>;
  store: MapStore<Record<string, T>>;
};

export type ResourceAsyncSelectCacheReturn<T> = {
  getById: (id: string) => T | undefined;
  isLoading: boolean;
  loadById: (id: string) => Promise<null | T>;
  loadOptions: (inputValue: string) => Promise<T[]>;
  record: Record<string, T>;
  runWithLoading: <R>(fn: () => Promise<R>) => Promise<R>;
  searchResults: T[];
  select: (item: T) => void;
};

export const useResourceAsyncSelectCache = <T extends { id: string }>(
  config: ResourceAsyncSelectCacheConfig<T>,
): ResourceAsyncSelectCacheReturn<T> => {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    fetchById,
    loadDefaultBatch,
    maxDefaultItems = DEFAULT_MAX_DEFAULT_ITEMS,
    maxSearchItems = DEFAULT_MAX_SEARCH_ITEMS,
    messages,
    search,
    store,
  } = config;

  const record = useStore(store);
  /** Bumps when a debounced search is superseded so stale work does not touch state. */
  const debouncedSearchGeneration = useRef(0);
  /** Resolve for the active debounced-search Promise (react-select awaits this). */
  const pendingSearchResolve = useRef<((value: T[]) => void) | null>(null);
  const performSearchRef = useRef<(term: string) => Promise<T[]>>(
    async () => [],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<T[]>([]);

  const getById = useCallback(
    (id: string): T | undefined => {
      return store.get()[id];
    },
    [store],
  );

  const select = useCallback(
    (item: T): void => {
      store.setKey(item.id, item);
    },
    [store],
  );

  const runWithLoading = useCallback(
    async <R>(fn: () => Promise<R>): Promise<R> => {
      setIsLoading(true);
      try {
        return await fn();
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const loadById = useCallback(
    async (id: string): Promise<null | T> => {
      return runWithLoading(async () => {
        const cached = getById(id);
        if (cached) {
          return cached;
        }

        try {
          const data = await fetchById(id);
          if (data) {
            store.setKey(data.id, data);
            return data;
          }
        } catch (err) {
          console.error(messages.fetchSelected, err);
        }

        return null;
      });
    },
    [fetchById, getById, messages.fetchSelected, runWithLoading, store],
  );

  const performSearch = useCallback(
    async (searchTerm: string): Promise<T[]> => {
      try {
        const data = await search(searchTerm);
        return data.slice(0, maxSearchItems);
      } catch (err) {
        console.error(messages.search, err);
        return [];
      }
    },
    [maxSearchItems, messages.search, search],
  );

  performSearchRef.current = performSearch;

  const debouncedSearch = useMemo(
    () =>
      debounce(async (inputValue: string): Promise<T[]> => {
        const snapshotGen = debouncedSearchGeneration.current;
        setIsLoading(true);
        try {
          return await performSearchRef.current(inputValue);
        } finally {
          if (debouncedSearchGeneration.current === snapshotGen) {
            setIsLoading(false);
          }
        }
      }, debounceMs),
    [debounceMs],
  );

  const cancelPendingDebouncedSearch = useCallback((): void => {
    debouncedSearchGeneration.current += 1;
    debouncedSearch.cancel();
    const resolveStale = pendingSearchResolve.current;
    pendingSearchResolve.current = null;
    resolveStale?.([]);
  }, [debouncedSearch]);

  useEffect(() => {
    return (): void => {
      debouncedSearch.cancel();
      const resolveStale = pendingSearchResolve.current;
      pendingSearchResolve.current = null;
      resolveStale?.([]);
    };
  }, [debouncedSearch]);

  const loadOptions = useCallback(
    async (inputValue: string): Promise<T[]> => {
      cancelPendingDebouncedSearch();

      if (inputValue) {
        return new Promise<T[]>((resolve) => {
          pendingSearchResolve.current = resolve;
          const myGeneration = debouncedSearchGeneration.current;
          void debouncedSearch(inputValue).then(
            (results) => {
              if (debouncedSearchGeneration.current !== myGeneration) {
                return;
              }
              pendingSearchResolve.current = null;
              setSearchResults(results);
              resolve(results);
            },
            () => {
              if (debouncedSearchGeneration.current !== myGeneration) {
                return;
              }
              pendingSearchResolve.current = null;
              resolve([]);
            },
          );
        });
      }

      const cached = Object.values(record).slice(0, maxDefaultItems);
      if (cached.length > 0) {
        setSearchResults([]);
        setIsLoading(false);
        return cached;
      }

      setIsLoading(true);
      try {
        const items = await loadDefaultBatch();
        items.forEach((item) => store.setKey(item.id, item));
        setSearchResults([]);
        return items;
      } catch (err) {
        console.error(messages.loadInitial, err);
        setSearchResults([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [
      cancelPendingDebouncedSearch,
      debouncedSearch,
      loadDefaultBatch,
      maxDefaultItems,
      messages.loadInitial,
      record,
      store,
    ],
  );

  return {
    getById,
    isLoading,
    loadById,
    loadOptions,
    record,
    runWithLoading,
    searchResults,
    select,
  };
};
